import { useEffect, useRef, useState } from 'react';
import { ApiError, receptionApi, type MessageView, type OfferAction, type OfferView } from '../api/client';
import { clockText, dial, expectCall, prettyNumber, useCalls } from '../lib/calls';
import { clockTime } from '../lib/format';
import { Icon } from '../lib/icons';
import { useMessagesLive } from '../lib/messages';
import { Button } from './ui';

/**
 * Call for you (Ask by message, 1.13.0): a receptionist asks whether someone will take a call while
 * the caller holds. The card sits in their direct messages with the receptionist: Take it (the call
 * comes to their extension), Take it here (and this Stitch answers it), Voicemail, Not now, or Give
 * me 2 minutes. It stays as a record of how it ended. The receptionist sees the same card, waiting.
 */

const LIVE = ['sent', 'seen', 'replied'];
const first = (name: string) => name.split(/\s+/)[0] || name;

function useTick(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [on]);
  return now;
}

export function CallOfferCard({ domain, message, offerId, initial, myId }: { domain: string; message?: MessageView; offerId?: string; initial?: OfferView; myId: string }) {
  const id = offerId ?? (typeof message?.meta?.offerId === 'string' ? message.meta.offerId : '');
  const [offer, setOffer] = useState<OfferView | null>(initial ?? null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<OfferAction | null>(null);
  /** Taken here, on this device (the record says so). */
  const [here, setHere] = useState(false);
  const seen = useRef(false);
  const calls = useCalls();
  useEffect(() => {
    if (!id) return;
    let live = true;
    receptionApi
      .offer(domain, id)
      .then((r) => live && setOffer(r.offer))
      .catch((err) => live && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, [domain, id]);
  useMessagesLive((e) => {
    if (e.type === 'offer' && e.offer?.id === id) setOffer(e.offer);
  }, Boolean(id));
  const open = Boolean(offer && LIVE.includes(offer.state));
  const now = useTick(open);
  const mine = offer?.to.id === myId;
  // They've seen it (the receptionist's card says so).
  useEffect(() => {
    if (!offer || !mine || offer.state !== 'sent' || seen.current || document.hidden) return;
    seen.current = true;
    void receptionApi.answer(domain, offer.id, 'seen').catch(() => undefined);
  }, [offer, mine, domain]);

  if (!id) return null;
  if (!offer) return <div className="call-offer muted">{error ?? 'Call for you…'}</div>;

  const act = async (action: OfferAction) => {
    setBusy(action);
    setError(null);
    // Take it here: this Stitch answers the call when it rings (it's on its way).
    if (action === 'here') expectCall(offer.caller.number);
    try {
      const r = await receptionApi.answer(domain, offer.id, action);
      setOffer(r.offer);
      if (action === 'here') setHere(true);
    } catch (err) {
      if (action === 'here') expectCall('');
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const caller = offer.caller.name || prettyNumber(offer.caller.number) || 'A caller';
  const held = clockText(Math.max(0, Math.round((now - Date.parse(offer.createdAt)) / 1000)));
  const left = Math.max(0, Math.round((Date.parse(offer.expiresAt) - now) / 1000));
  const at = clockTime(offer.updatedAt);
  const canHere = calls.on && calls.available;
  const done = (() => {
    switch (offer.state) {
      case 'accepted':
        return mine ? (here ? `You took it here at ${at}.` : `You took it at ${at}. Your phones are ringing.`) : `${first(offer.to.name)} took it at ${at}.`;
      case 'voicemail':
        return `Sent to ${mine ? 'your' : `${first(offer.to.name)}’s`} voicemail at ${at}.`;
      case 'declined':
        return mine ? `You said Not now. ${first(offer.from.name)} has the caller.` : `${first(offer.to.name)} said Not now.`;
      case 'expired':
        return mine ? `No answer in time. ${first(offer.from.name)} has the caller.` : `No answer from ${first(offer.to.name)}.`;
      case 'withdrawn':
        return 'Reception took the call back.';
      case 'caller_left':
        return 'Caller hung up.';
      case 'failed':
        return 'The call couldn’t be sent on. Reception still has it.';
      default:
        return '';
    }
  })();

  return (
    <div className={`call-offer${open ? ' open' : ''}`} role="group" aria-label={`Call for you: ${caller}`}>
      <div className="call-offer-head">
        <Icon name="phone" size={16} />
        <strong className="grow">Call for you</strong>
        {open && <span className="call-offer-held">Holding {held}</span>}
      </div>
      <div className="cell-stack">
        <strong>{caller}</strong>
        {offer.caller.name && offer.caller.number && <small className="muted">{prettyNumber(offer.caller.number)}</small>}
      </div>
      {offer.note && <p className="call-offer-note">{offer.note}</p>}
      {open && offer.state === 'replied' && (
        <p className="muted call-offer-line">{mine ? `You said Give me 2 minutes. ${first(offer.from.name)} can tell the caller.` : `${first(offer.to.name)} said Give me 2 minutes.`}</p>
      )}
      {open && mine && (
        <div className="call-offer-actions">
          <Button size="small" variant="primary" disabled={Boolean(busy)} onClick={() => void act('take')}>
            Take it
          </Button>
          {canHere && (
            <Button size="small" variant="primary" disabled={Boolean(busy)} onClick={() => void act('here')}>
              Take it here
            </Button>
          )}
          <Button size="small" disabled={Boolean(busy)} onClick={() => void act('voicemail')}>
            Voicemail
          </Button>
          <Button size="small" disabled={Boolean(busy)} onClick={() => void act('not-now')}>
            Not now
          </Button>
          {offer.state !== 'replied' && (
            <Button size="small" variant="ghost" disabled={Boolean(busy)} onClick={() => void act('later')}>
              Give me 2 minutes
            </Button>
          )}
        </div>
      )}
      {open && !mine && (
        <div className="call-offer-actions">
          <Button size="small" disabled={Boolean(busy)} onClick={() => void act('withdraw')}>
            Take it back
          </Button>
        </div>
      )}
      {open && (
        <small className="muted">
          {mine ? `${first(offer.from.name)} is waiting` : `Waiting for ${first(offer.to.name)}`} · {left} s left
        </small>
      )}
      {!open && done && (
        <p className="call-offer-done">
          {done}
          {offer.state === 'caller_left' && mine && offer.caller.number && (
            <>
              {' '}
              <Button size="small" icon="phone" onClick={() => void dial(offer.caller.number)}>
                Call back
              </Button>
            </>
          )}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
